import { useRef, useState } from 'react';
import { ArrowPathIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import { tenderApi } from '../../services/api';
import {
  EVALUATION_METHOD_LABELS,
  INDUSTRY_LABELS,
  PRICE_FORMULA_LABELS,
  PURCHASE_METHOD_LABELS,
  type TenderEvaluationMethod,
  type TenderIndustry,
  type TenderInfoPayload,
  type TenderPriceFormula,
  type TenderPurchaseMethod,
  type TenderStructureNode,
  type TenderTemplate,
  type TenderTemplateParseResult,
} from '../../types/tender';

/** 表单状态（新建与编辑共用；数字/结构化字段用文本框承载，提交时再转换） */
export interface TenderFormState {
  name: string;
  template_id: number | null;
  purchaser: string;
  agency: string;
  project_no: string;
  budget: string;
  price_cap: string;
  duration: string;
  location: string;
  purchase_method: TenderPurchaseMethod;
  industry: TenderIndustry;
  evaluation_method: TenderEvaluationMethod;
  price_formula: TenderPriceFormula;
  price_score: string;
  deposit: string;
  bid_deadline_days: string;
  legal_basis: string;
  notes: string;
  /** 每行一个包件：名称|内容|预算 */
  packagesText: string;
  /** 每行一条资格条件：类别|条件描述 */
  qualificationText: string;
  /** 每行一条技术要求：类别|要求（编制技术规格书时逐条落实） */
  technicalText: string;
  /** 每行一条评分要求：评分因素|分值|评分标准（编制评标办法时据此生成评分表） */
  scoringText: string;
}

export const emptyTenderForm = (): TenderFormState => ({
  name: '',
  template_id: null,
  purchaser: '',
  agency: '',
  project_no: '',
  budget: '',
  price_cap: '',
  duration: '',
  location: '',
  purchase_method: 'open',
  industry: 'engineering',
  evaluation_method: 'comprehensive',
  price_formula: 'lowest',
  price_score: '',
  deposit: '',
  bid_deadline_days: '20',
  legal_basis: '',
  notes: '',
  packagesText: '',
  qualificationText: '',
  technicalText: '',
  scoringText: '',
});

const parsePackages = (text: string): Array<{ name: string; content: string; budget: string }> =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name = '', content = '', budget = ''] = line.split('|').map((part) => part.trim());
      return { name, content, budget };
    });

const parseQualification = (text: string): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const index = line.indexOf('|');
      const key = index >= 0 ? line.slice(0, index).trim() : '其他';
      const value = index >= 0 ? line.slice(index + 1).trim() : line;
      if (!value) {
        return;
      }
      result[key] = [...(result[key] || []), value];
    });
  return result;
};

/** 每行一条评分要求：评分因素|分值|评分标准 */
const parseScoring = (
  text: string,
): Array<{ item: string; score: string; standard: string }> =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [item = '', score = '', standard = ''] = line.split('|').map((part) => part.trim());
      return { item, score, standard };
    });

/** 表单 → 接口 payload
 *
 * 空字符串会被提交（表示「清空该字段」），否则用户删掉内容后保存会因「空值不提交」
 * 而看起来"没保存"；数字解析失败与未选择的字段才留空不提交。
 */
export const buildInfoPayload = (form: TenderFormState): TenderInfoPayload => {
  const packages = parsePackages(form.packagesText);
  const qualification = parseQualification(form.qualificationText);
  const priceScore = Number.parseInt(form.price_score, 10);
  const deadline = Number.parseInt(form.bid_deadline_days, 10);
  return {
    purchaser: form.purchaser.trim(),
    agency: form.agency.trim(),
    project_no: form.project_no.trim(),
    budget: form.budget.trim(),
    price_cap: form.price_cap.trim(),
    duration: form.duration.trim(),
    location: form.location.trim(),
    purchase_method: form.purchase_method,
    industry: form.industry,
    evaluation_method: form.evaluation_method,
    price_formula: form.price_formula,
    price_score: Number.isNaN(priceScore) ? undefined : priceScore,
    deposit: form.deposit.trim(),
    bid_deadline_days: Number.isNaN(deadline) ? undefined : deadline,
    legal_basis: form.legal_basis.trim(),
    notes: form.notes.trim(),
    packages,
    qualification,
    technical_requirements: parseQualification(form.technicalText),
    scoring_requirements: parseScoring(form.scoringText),
  };
};

const serializePackages = (packages?: Array<{ name?: string; content?: string; budget?: string }>) =>
  (packages || [])
    .map((item) => [item.name || '', item.content || '', item.budget || ''].join('|'))
    .join('\n');

const serializeQualification = (qualification?: Record<string, string[]>) =>
  Object.entries(qualification || {})
    .flatMap(([key, values]) => (values || []).map((value) => `${key}|${value}`))
    .join('\n');

const serializeScoring = (
  items?: Array<{ item?: string; score?: string; standard?: string }>,
) =>
  (items || [])
    .map((item) => [item.item || '', item.score || '', item.standard || ''].join('|'))
    .join('\n');

/** 已有立项信息 → 表单状态 */
export const formFromInfo = (
  name: string,
  info: Partial<{
    purchaser: string | null;
    agency: string | null;
    project_no: string | null;
    budget: string | null;
    price_cap: string | null;
    duration: string | null;
    location: string | null;
    purchase_method: TenderPurchaseMethod;
    industry: TenderIndustry;
    evaluation_method: TenderEvaluationMethod;
    price_formula: TenderPriceFormula;
    price_score: number;
    deposit: string | null;
    bid_deadline_days: number;
    legal_basis: string | null;
    notes: string | null;
    packages: Array<{ name?: string; content?: string; budget?: string }>;
    qualification: Record<string, string[]>;
    technical_requirements: Record<string, string[]>;
    scoring_requirements: Array<{ item?: string; score?: string; standard?: string }>;
  }> | null,
): TenderFormState => {
  const base = emptyTenderForm();
  if (!info) {
    return { ...base, name };
  }
  return {
    ...base,
    name,
    purchaser: info.purchaser || '',
    agency: info.agency || '',
    project_no: info.project_no || '',
    budget: info.budget || '',
    price_cap: info.price_cap || '',
    duration: info.duration || '',
    location: info.location || '',
    purchase_method: info.purchase_method || base.purchase_method,
    industry: info.industry || base.industry,
    evaluation_method: info.evaluation_method || base.evaluation_method,
    price_formula: info.price_formula || base.price_formula,
    price_score: info.price_score ? String(info.price_score) : '',
    deposit: info.deposit || '',
    bid_deadline_days: info.bid_deadline_days ? String(info.bid_deadline_days) : '20',
    legal_basis: info.legal_basis || '',
    notes: info.notes || '',
    packagesText: serializePackages(info.packages),
    qualificationText: serializeQualification(info.qualification),
    technicalText: serializeQualification(info.technical_requirements),
    scoringText: serializeScoring(info.scoring_requirements),
  };
};

interface TenderProjectFormProps {
  mode: 'create' | 'edit';
  form: TenderFormState;
  onChange: (next: TenderFormState) => void;
  templates: TenderTemplate[];
  submitting: boolean;
  onSubmit: () => void;
  onCancel?: () => void;
  /** 新建模板成功后通知父级刷新模板列表 */
  onTemplatesChanged?: () => void;
}

const inputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400';

const selectClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400';

/** 招标文件立项信息表单（新建时含模板选择） */
export default function TenderProjectForm({
  mode,
  form,
  onChange,
  templates,
  submitting,
  onSubmit,
  onCancel,
  onTemplatesChanged,
}: TenderProjectFormProps) {
  const patch = (partial: Partial<TenderFormState>) => onChange({ ...form, ...partial });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseNotice, setParseNotice] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<TenderTemplateParseResult | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const groupedTemplates = templates.reduce<Record<string, TenderTemplate[]>>((acc, item) => {
    const key = `${item.purchase_method}/${item.industry}`;
    acc[key] = [...(acc[key] || []), item];
    return acc;
  }, {});

  const flattenPreview = (structure: TenderStructureNode[], depth = 0): string[] =>
    structure.flatMap((item) => [
      `${'　'.repeat(depth)}${item.title}`,
      ...flattenPreview(item.children || [], depth + 1),
    ]);

  const handleParseFile = async (file?: File) => {
    if (!file) {
      return;
    }
    setParsing(true);
    setParseError(null);
    setParseNotice(null);
    setParseResult(null);
    try {
      const res = await tenderApi.parseTemplateFile(file);
      setParseResult(res.data.data);
      setParseNotice(
        `已解析 ${res.data.data.node_count} 个章节（${
          res.data.data.source === 'markdown' ? '按标题层级' : '模型解析'
        }），确认后可保存为模板`,
      );
    } catch (err) {
      setParseError(err instanceof Error ? err.message : '解析失败，请确认文件为招标文件正文');
    } finally {
      setParsing(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!parseResult) {
      return;
    }
    setSavingTemplate(true);
    setParseError(null);
    try {
      const res = await tenderApi.createTemplate({
        name: parseResult.name || '历史招标文件模板',
        purchase_method: form.purchase_method,
        industry: form.industry,
        description: '由历史招标文件解析生成',
        structure: parseResult.structure,
      });
      patch({ template_id: res.data.data.id });
      onTemplatesChanged?.();
      setParseResult(null);
      setParseNotice(`模板「${res.data.data.name}」已保存并选中`);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : '保存模板失败');
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <div className="space-y-4">
      {mode === 'create' && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-600">
            选择模板（决定目录结构与章节口径）
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            {Object.entries(groupedTemplates).map(([key, list]) => (
              <div key={key} className="rounded-lg border border-gray-200 p-2">
                <p className="text-xs font-medium text-gray-500">
                  {PURCHASE_METHOD_LABELS[list[0].purchase_method]} ·{' '}
                  {INDUSTRY_LABELS[list[0].industry]}
                </p>
                <div className="mt-1.5 space-y-1">
                  {list.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => patch({ template_id: item.id })}
                      className={`w-full rounded-lg border px-2.5 py-1.5 text-left text-xs transition ${
                        form.template_id === item.id
                          ? 'border-indigo-300 bg-indigo-50 text-indigo-800'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                      title={item.description || ''}
                    >
                      {item.name}
                      {item.is_builtin && <span className="ml-1 text-gray-400">（内置）</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {templates.length === 0 && (
              <p className="text-xs text-gray-400">暂无可用模板</p>
            )}
          </div>
        </div>
      )}

      {mode === 'create' && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs font-medium text-gray-600">
                没有合适模板？从历史招标文件生成
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                上传一份历史招标文件（docx / pdf / txt / md），系统按标题层级或模型解析出章节结构，
                确认后保存为自有模板。
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.doc,.pdf,.txt,.md,.markdown"
                className="hidden"
                onChange={(event) => {
                  void handleParseFile(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={parsing}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {parsing ? (
                  <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <DocumentArrowUpIcon className="h-3.5 w-3.5" />
                )}
                {parsing ? '解析中…' : '选择文件'}
              </button>
            </div>
          </div>

          {parseError && <p className="mt-2 text-xs text-rose-600">{parseError}</p>}
          {parseNotice && !parseResult && (
            <p className="mt-2 text-xs text-emerald-700">{parseNotice}</p>
          )}

          {parseResult && (
            <div className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <input
                  value={parseResult.name}
                  onChange={(event) =>
                    setParseResult({ ...parseResult, name: event.target.value })
                  }
                  className="min-w-[220px] flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                  placeholder="模板名称"
                />
                <button
                  type="button"
                  onClick={() => void handleSaveTemplate()}
                  disabled={savingTemplate}
                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {savingTemplate ? '保存中…' : '保存为模板并选中'}
                </button>
              </div>
              {parseNotice && <p className="text-xs text-emerald-700">{parseNotice}</p>}
              <div className="max-h-40 overflow-y-auto rounded-lg bg-gray-50 p-2 font-mono text-[11px] leading-relaxed text-gray-600">
                {flattenPreview(parseResult.structure)
                  .slice(0, 40)
                  .map((line, index) => (
                    <p key={`${line}-${index}`}>{line}</p>
                  ))}
                {flattenPreview(parseResult.structure).length > 40 && (
                  <p className="text-gray-400">…（更多章节已省略）</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-600">项目名称</label>
          <input
            value={form.name}
            onChange={(event) => patch({ name: event.target.value })}
            placeholder="如：××道路改造工程施工招标"
            className={inputClass}
            disabled={mode === 'edit'}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">项目编号</label>
          <input
            value={form.project_no}
            onChange={(event) => patch({ project_no: event.target.value })}
            placeholder="如：ZB-2026-001"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">招标人（采购人）</label>
          <input
            value={form.purchaser}
            onChange={(event) => patch({ purchaser: event.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">招标代理机构</label>
          <input
            value={form.agency}
            onChange={(event) => patch({ agency: event.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">采购方式</label>
          <select
            value={form.purchase_method}
            onChange={(event) =>
              patch({ purchase_method: event.target.value as TenderPurchaseMethod })
            }
            className={selectClass}
          >
            {Object.entries(PURCHASE_METHOD_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">行业类别</label>
          <select
            value={form.industry}
            onChange={(event) => patch({ industry: event.target.value as TenderIndustry })}
            className={selectClass}
          >
            {Object.entries(INDUSTRY_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">预算金额</label>
          <input
            value={form.budget}
            onChange={(event) => patch({ budget: event.target.value })}
            placeholder="如：1200 万元"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">最高限价</label>
          <input
            value={form.price_cap}
            onChange={(event) => patch({ price_cap: event.target.value })}
            placeholder="如：1150 万元"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">工期/服务期</label>
          <input
            value={form.duration}
            onChange={(event) => patch({ duration: event.target.value })}
            placeholder="如：180 日历天"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">项目地点</label>
          <input
            value={form.location}
            onChange={(event) => patch({ location: event.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">评标办法</label>
          <select
            value={form.evaluation_method}
            onChange={(event) =>
              patch({ evaluation_method: event.target.value as TenderEvaluationMethod })
            }
            className={selectClass}
          >
            {Object.entries(EVALUATION_METHOD_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">价格分口径</label>
          <select
            value={form.price_formula}
            onChange={(event) =>
              patch({ price_formula: event.target.value as TenderPriceFormula })
            }
            className={selectClass}
          >
            {Object.entries(PRICE_FORMULA_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">价格分满分</label>
          <input
            value={form.price_score}
            onChange={(event) => patch({ price_score: event.target.value })}
            placeholder="如：30（0 表示未设定）"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">投标保证金</label>
          <input
            value={form.deposit}
            onChange={(event) => patch({ deposit: event.target.value })}
            placeholder="如：20 万元"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">
            投标截止时限（日，法定不少于 20 日）
          </label>
          <input
            value={form.bid_deadline_days}
            onChange={(event) => patch({ bid_deadline_days: event.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">依据法规/集团制度</label>
          <input
            value={form.legal_basis}
            onChange={(event) => patch({ legal_basis: event.target.value })}
            placeholder="如：《招标投标法》及实施条例、集团采购管理办法"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-600">
            包件划分（每行一个：名称|内容|预算）
          </label>
          <textarea
            value={form.packagesText}
            onChange={(event) => patch({ packagesText: event.target.value })}
            rows={3}
            placeholder={'一包|道路及排水工程|600 万元\n二包|照明工程|300 万元'}
            className={`${inputClass} font-mono text-xs`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">
            资格条件（每行一条：类别|条件描述）
          </label>
          <textarea
            value={form.qualificationText}
            onChange={(event) => patch({ qualificationText: event.target.value })}
            rows={3}
            placeholder={'资质等级|市政公用工程施工总承包二级及以上\n业绩|近 3 年完成单项合同额 800 万元以上类似工程 2 项\n人员|项目经理具备市政公用工程二级及以上注册建造师'}
            className={`${inputClass} font-mono text-xs`}
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-600">
            技术要求（每行一条：类别|要求）
          </label>
          <textarea
            value={form.technicalText}
            onChange={(event) => patch({ technicalText: event.target.value })}
            rows={4}
            placeholder={
              '技术标准|执行《城市道路工程施工与质量验收规范》CJJ 1-2008\n材料要求|管材采用 HDPE 双壁波纹管，环刚度不低于 SN8\n质量标准|一次验收合格率 100%，缺陷责任期 24 个月'
            }
            className={`${inputClass} font-mono text-xs`}
          />
          <p className="mt-1 text-[11px] text-gray-400">
            编制「技术规格书 / 采购需求」章节时逐条落实，不得降低标准或改变指标口径。
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">
            评分要求（每行一条：评分因素|分值|评分标准）
          </label>
          <textarea
            value={form.scoringText}
            onChange={(event) => patch({ scoringText: event.target.value })}
            rows={4}
            placeholder={
              '施工组织设计|20|方案完整可行、针对性强得 16-20 分\n项目管理机构|10|人员配置齐全、类似业绩满足要求\n价格分|30|按最低价优先公式计算'
            }
            className={`${inputClass} font-mono text-xs`}
          />
          <p className="mt-1 text-[11px] text-gray-400">
            编制「评标办法 / 评审办法」章节时按给定评分项与分值生成评分表（分值合计建议 100 分）。
          </p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600">
          其他说明（会作为补充要求传给模型）
        </label>
        <textarea
          value={form.notes}
          onChange={(event) => patch({ notes: event.target.value })}
          rows={2}
          placeholder="如：本项目为民生工程，要求工期不得顺延；技术规格书须允许同等产品"
          className={inputClass}
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || !form.name.trim() || (mode === 'create' && !form.template_id)}
          className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
        >
          {submitting && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
          {mode === 'create' ? '创建并生成目录' : '保存立项信息'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            取消
          </button>
        )}
      </div>
    </div>
  );
}
